import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { toast } from "sonner";
import anime from "animejs";
import {
  Sparkle, LockKey, Star, Crown, Lightning, ArrowRight, ArrowLeft,
  Wallet, CheckCircle, Path as PathIcon, FastForward, Confetti, ShareNetwork, Eye, Calendar,
} from "@phosphor-icons/react";
import { PageShell } from "../components/Layout";
import { useAuth } from "../context/AuthContext";
import { useCurrency } from "../context/CurrencyContext";
import { useToolUnlock } from "../lib/toolUnlock";
import PlaceAutocompleteInput from "../components/PlaceAutocompleteInput";
import usePrefillBirthDetails from "../hooks/usePrefillBirthDetails";
import usePlatform from "../hooks/usePlatform";
import { startGoogleSignIn } from "../lib/capacitorAuth";
import api from "../lib/api";

const TOOL = "astro_visualization";

/* Base scene painted inline with SVG — no external image, palette-responsive. */

/* Planet-themed palettes — used both for the scene tint and signpost styling. */
const PALETTES = {
  sun:     { name: "Sun",     tintFrom: "#7A2B00", tintTo: "#F59E0B", accent: "#FDE047", ring: "#F59E0B", ink: "#FEF3C7", glyph: "☉" },
  moon:    { name: "Moon",    tintFrom: "#0B1B36", tintTo: "#60A5FA", accent: "#DBEAFE", ring: "#60A5FA", ink: "#DBEAFE", glyph: "☾" },
  mars:    { name: "Mars",    tintFrom: "#450A0A", tintTo: "#DC2626", accent: "#FCA5A5", ring: "#EF4444", ink: "#FEE2E2", glyph: "♂" },
  mercury: { name: "Mercury", tintFrom: "#052E1A", tintTo: "#10B981", accent: "#6EE7B7", ring: "#10B981", ink: "#D1FAE5", glyph: "☿" },
  jupiter: { name: "Jupiter", tintFrom: "#3C1A00", tintTo: "#F59E0B", accent: "#FDE68A", ring: "#F59E0B", ink: "#FEF3C7", glyph: "♃" },
  venus:   { name: "Venus",   tintFrom: "#4A0D3A", tintTo: "#EC4899", accent: "#FBCFE8", ring: "#EC4899", ink: "#FCE7F3", glyph: "♀" },
  saturn:  { name: "Saturn",  tintFrom: "#0A0F1F", tintTo: "#475569", accent: "#94A3B8", ring: "#64748B", ink: "#E2E8F0", glyph: "♄" },
  rahu:    { name: "Rahu",    tintFrom: "#1A0724", tintTo: "#7C3AED", accent: "#C4B5FD", ring: "#7C3AED", ink: "#EDE9FE", glyph: "☊" },
  ketu:    { name: "Ketu",    tintFrom: "#111827", tintTo: "#9CA3AF", accent: "#D1D5DB", ring: "#9CA3AF", ink: "#F3F4F6", glyph: "☋" },
};

/* Feature list for the locked hero */
const FEATURES = [
  { icon: PathIcon,    title: "Your life as a road journey",       body: "Cinematic scroll through every past mahadasha — from little kid to today — signposted like real milestones on a real road." },
  { icon: Sparkle,     title: "Planet-themed atmosphere",           body: "Tap any phase and the entire scene morphs to that planet's palette — Jupiter is golden warmth, Saturn is disciplined slate, Venus is rose-glow romance." },
  { icon: Star,        title: "Placement-driven readings",          body: "Each phase reads what that planet actually does in YOUR chart — which house it sits in, which houses it lords, what karmic domain it's activating." },
  { icon: Lightning,   title: "Antardasha key-event map",           body: "Rule-based combos surface the events each sub-period pushed — Jupiter–Venus = relationships bloom; Saturn–Ketu = career shift; Rahu–Jupiter = foreign expansion." },
  { icon: FastForward, title: "See your future arc",                body: "Once unlocked, reveal the next 6 mahadashas — the same visual language, forward in time, so you can plan the road ahead." },
  { icon: Crown,       title: "One-time · lifetime for this chart", body: "One payment of ₹5,000 (+ 18% GST) unlocks the full life-journey for this birth chart. Forever. No subscription." },
];

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
const MINUTES = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, "0"));

// Life stage → SVG silhouette metadata
const STAGE_SILHOUETTES = {
  kid:     { height: 60,  scale: 0.9, walkSpeed: 1.6 },
  teen:    { height: 78,  scale: 1.0, walkSpeed: 1.4 },
  youth:   { height: 96,  scale: 1.05, walkSpeed: 1.2 },
  adult:   { height: 108, scale: 1.1, walkSpeed: 1.0 },
  midlife: { height: 112, scale: 1.15, walkSpeed: 1.0 },
  elder:   { height: 108, scale: 1.05, walkSpeed: 0.9 },
};

export default function AstroVisualizationPage() {
  const { user, checkAuth } = useAuth();
  const { currency, formatPrice } = useCurrency();
  const [form, setForm] = useState({ date: "", time: "", place: "", timezone: "Asia/Kolkata" });
  usePrefillBirthDetails(setForm);
  const [name, setName] = useState("");
  useEffect(() => { if (user?.name && !name) setName(user.name); }, [user, name]); // eslint-disable-line

  const isSuperAdmin = !!user?.is_super_admin;
  const hasBirth = !!(form.date && form.time && form.place);
  const {
    unlocked, loading: unlockLoading, unlockPrice, walletBalance, paying,
    payWithWallet, payWithRazorpay,
  } = useToolUnlock({
    tool: TOOL, birthDetails: form, user,
    onSuccess: () => { checkAuth?.(); },
  });
  const effectivelyUnlocked = isSuperAdmin || (hasBirth && unlocked);
  const gstAmount = Math.round(unlockPrice * 0.18 * 100) / 100;
  const totalWithGst = Math.round((unlockPrice + gstAmount) * 100) / 100;
  const isUsd = currency === "USD";
  const taxLabel = isUsd ? "Taxes (18%)" : "GST (18%)";

  const [journey, setJourney] = useState(null);
  const [journeyBusy, setJourneyBusy] = useState(false);
  const [showFuture, setShowFuture] = useState(false);
  const [futureData, setFutureData] = useState(null);
  const [futureBusy, setFutureBusy] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(null);
  // Selection can be either a past/current signpost (from allSignposts) OR a future one.
  // We use a discriminated state: {source: "past"|"future", index: number}
  const [selectedFutureIndex, setSelectedFutureIndex] = useState(null);

  useEffect(() => {
    setJourney(null); setFutureData(null); setShowFuture(false); setSelectedIndex(null); setSelectedFutureIndex(null);
  }, [form.date, form.time, form.place, form.timezone]);

  useEffect(() => {
    if (!effectivelyUnlocked) return;
    if (journey || journeyBusy) return;
    if (!hasBirth) return;
    (async () => {
      setJourneyBusy(true);
      try {
        const { data } = await api.post("/astro-viz/compute", {
          birth_details: form, name: name || user?.name || "You", include_future: false,
        });
        setJourney(data.journey);
        if (data?.journey?.past?.length) {
          // Default select current mahadasha (last past + 1) or the last past
          const cur = data.journey.current;
          setSelectedIndex(cur ? data.journey.past.length : data.journey.past.length - 1);
        }
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Could not build your life-journey.");
      } finally {
        setJourneyBusy(false);
      }
    })();
  }, [effectivelyUnlocked, hasBirth, form, journey, journeyBusy, name, user?.name]);

  const revealFuture = async () => {
    if (futureData) { setShowFuture(true); return; }
    setFutureBusy(true);
    try {
      const { data } = await api.post("/astro-viz/compute", {
        birth_details: form, name: name || user?.name || "You", include_future: true,
      });
      setFutureData(data.journey.future || []);
      setShowFuture(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not compute your future arc.");
    } finally {
      setFutureBusy(false);
    }
  };

  const allSignposts = useMemo(() => {
    if (!journey) return [];
    const past = journey.past || [];
    const cur = journey.current ? [{ ...journey.current, _isCurrent: true }] : [];
    return [...past, ...cur];
  }, [journey]);

  const activeSign = selectedFutureIndex != null && futureData
    ? { ...futureData[selectedFutureIndex], _isFuture: true }
    : (selectedIndex != null ? allSignposts[selectedIndex] : null);
  const activePalette = activeSign ? PALETTES[activeSign.palette] : PALETTES.jupiter;

  // Wrap the past selector so it clears the future selection (mutually-exclusive selection).
  const selectPast = (i) => { setSelectedFutureIndex(null); setSelectedIndex(i); };
  const selectFuture = (i) => { setSelectedIndex(null); setSelectedFutureIndex(i); };

  return (
    <PageShell>
      <div data-testid="astro-viz-page" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 pt-8 pb-24">
        {!effectivelyUnlocked ? (
          <LockedHero
            user={user}
            form={form} setForm={setForm}
            hasBirth={hasBirth}
            unlockPrice={unlockPrice}
            totalWithGst={totalWithGst}
            gstAmount={gstAmount}
            taxLabel={taxLabel}
            walletBalance={walletBalance}
            paying={paying}
            unlockLoading={unlockLoading}
            payWithWallet={payWithWallet}
            payWithRazorpay={payWithRazorpay}
            formatPrice={formatPrice}
          />
        ) : (
          <UnlockedView
            journey={journey}
            journeyBusy={journeyBusy}
            allSignposts={allSignposts}
            selectedIndex={selectedIndex}
            setSelectedIndex={selectPast}
            selectedFutureIndex={selectedFutureIndex}
            setSelectedFutureIndex={selectFuture}
            activeSign={activeSign}
            activePalette={activePalette}
            revealFuture={revealFuture}
            showFuture={showFuture}
            futureData={futureData}
            futureBusy={futureBusy}
            name={name || user?.name || "You"}
            isSuperAdmin={isSuperAdmin}
            form={form}
            setForm={setForm}
          />
        )}
      </div>
    </PageShell>
  );
}

// ----------------------------------------------------------------------------
// LOCKED HERO — pure static, zero API cost
// ----------------------------------------------------------------------------
function LockedHero({
  user, form, setForm, hasBirth, unlockPrice, totalWithGst, gstAmount,
  taxLabel, walletBalance, paying, unlockLoading, payWithWallet, payWithRazorpay,
  formatPrice,
}) {
  const canPayWithWallet = walletBalance >= totalWithGst;
  const heroRef = useRef(null);
  const { hidePurchases } = usePlatform();

  useEffect(() => {
    if (!heroRef.current) return;
    // anime.js entrance: title + subtitle + CTAs stagger in
    anime({
      targets: heroRef.current.querySelectorAll("[data-fade]"),
      opacity: [0, 1],
      translateY: [24, 0],
      delay: anime.stagger(80),
      duration: 700,
      easing: "easeOutCubic",
    });
  }, []);

  const handleLogin = () => {
    const redirectUrl = window.location.origin + "/astro-visualization";
    startGoogleSignIn(redirectUrl);
  };
  const doWallet = async () => {
    const r = await payWithWallet();
    if (!r?.ok) toast.error(r?.error || "Wallet unlock failed.");
    else toast.success("Unlocked!");
  };
  const doRazorpay = async () => {
    const r = await payWithRazorpay();
    if (r?.ok) toast.success("Unlocked!");
    else if (r?.error) toast.error(r.error);
  };

  return (
    <section data-testid="astro-viz-locked-hero" ref={heroRef} className="relative">
      {/* Cinematic hero panel using the photorealistic base scene */}
      <div className="relative overflow-hidden rounded-3xl border border-amber-500/20 shadow-2xl min-h-[380px]">
        {/* Photorealistic background */}
        <div
          className="absolute inset-0 bg-cover bg-center scale-110"
          style={{ backgroundImage: `url(/astro-viz/scene-with-characters.png)`, filter: "brightness(0.6) saturate(1.05)" }}
        />
        {/* Vignette + darken */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#0B0F1F]/70 via-transparent to-[#3A1B4C]/50" />
        <StarField dense />

        <div className="relative z-10 p-6 sm:p-10">
          <div data-fade className="inline-flex items-center gap-2 rounded-full border border-amber-400/40 bg-black/30 px-3 py-1 text-[10px] uppercase tracking-widest text-amber-300">
            <Sparkle size={12} weight="fill" /> World's first · Astro-Visual Platform
          </div>
          <h1 data-fade className="mt-4 font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-tight text-amber-50 font-light drop-shadow-lg">
            Your Life Journey <em className="text-amber-300 not-italic">by Mahadasha</em>
          </h1>
          <p data-fade className="mt-4 max-w-2xl text-amber-100/85 text-base sm:text-lg leading-relaxed font-light drop-shadow">
            A cinematic road-trip through your past — every mahadasha is a lit-up signpost on a winding
            journey from little kid to today. Tap any phase and the whole scene morphs to that planet's
            atmosphere.
          </p>
          <div data-fade className="mt-6 inline-flex items-center gap-3 rounded-full bg-amber-500/20 border border-amber-400/50 px-5 py-3 backdrop-blur">
            <LockKey size={18} weight="fill" className="text-amber-300" />
            <span className="text-amber-100 text-sm">Unlock this chart for {formatPrice ? formatPrice(unlockPrice) : `₹${unlockPrice}`}</span>
          </div>
          <div data-fade className="mt-2 text-[11px] text-amber-200/80">
            Per-chart, one-time. Lifetime access to your life-journey.
          </div>
        </div>
      </div>

      {/* Feature grid */}
      <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {FEATURES.map((f, i) => {
          const I = f.icon;
          return (
            <div key={i} data-fade className="rounded-2xl border border-amber-200/50 bg-white/70 p-4 flex gap-3 items-start" data-testid={`av-feature-${i}`}>
              <div className="shrink-0 w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center">
                <I size={18} weight="duotone" className="text-amber-700" />
              </div>
              <div>
                <div className="font-serif-display text-base text-[#3F2D1B]">{f.title}</div>
                <p className="text-[12px] text-muted leading-relaxed mt-0.5">{f.body}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Birth-details capture + unlock CTA */}
      <div data-fade className="mt-10 rounded-3xl border border-amber-200 bg-white p-5 sm:p-7 shadow-[0_4px_24px_rgba(63,45,27,0.05)]" data-testid="av-unlock-panel">
        <h2 className="font-serif-display text-2xl text-[#3F2D1B]">Unlock your life-journey</h2>
        <p className="text-muted text-sm mt-1">Enter your birth details — we'll bind the unlock to this chart forever.</p>

        <div className="mt-5 grid sm:grid-cols-2 gap-4">
          <label className="block">
            <span className="label-eyebrow text-xs">Date of birth</span>
            <input type="date" required value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} className="mt-1.5 w-full px-3 py-2.5 rounded-md border border-soft bg-bg" data-testid="av-date" />
          </label>
          <label className="block">
            <span className="label-eyebrow text-xs">Time of birth</span>
            <div className="mt-1.5 grid grid-cols-2 gap-2">
              <select value={form.time.split(":")[0] || ""} onChange={(e) => setForm((f) => ({ ...f, time: `${e.target.value}:${(f.time.split(":")[1] || "00")}` }))} className="px-3 py-2.5 rounded-md border border-soft bg-bg" data-testid="av-hour">
                <option value="">HH</option>
                {HOURS.map((h) => (<option key={h} value={h}>{h}</option>))}
              </select>
              <select value={form.time.split(":")[1] || ""} onChange={(e) => setForm((f) => ({ ...f, time: `${(f.time.split(":")[0] || "00")}:${e.target.value}` }))} className="px-3 py-2.5 rounded-md border border-soft bg-bg" data-testid="av-minute">
                <option value="">MM</option>
                {MINUTES.map((m) => (<option key={m} value={m}>{m}</option>))}
              </select>
            </div>
          </label>
          <label className="block sm:col-span-2">
            <span className="label-eyebrow text-xs">Place of birth</span>
            <div className="mt-1.5">
              <PlaceAutocompleteInput
                value={form.place}
                onChange={(v) => setForm((f) => ({ ...f, place: v }))}
                onPick={({ name, timezone }) => setForm((f) => ({ ...f, place: name, timezone: timezone || f.timezone }))}
                placeholder="Mumbai, India"
                testId="av-place"
              />
            </div>
          </label>
        </div>

        {!user ? (
          <button onClick={handleLogin} className="mt-6 btn-primary rounded-full px-6 py-2.5 text-sm font-medium" data-testid="av-signin-btn">
            Sign in to unlock
          </button>
        ) : !hasBirth ? (
          <div className="mt-6 text-sm text-muted italic">Fill your birth details above to see unlock options.</div>
        ) : hidePurchases ? (
          <div className="mt-6 rounded-2xl bg-amber-50 border border-amber-200 p-5 text-sm text-[#3F2D1B]" data-testid="av-ios-reader-mode">
            <div className="font-medium">Unlock on astrokuberchat.com</div>
            <p className="text-muted mt-1 font-light">
              To keep the iOS app light and private, the full Astro Visualization
              unlocks on the website. Once unlocked there, this page opens
              instantly on your next refresh.
            </p>
            <button
              onClick={() => { try { window.open("https://astrokuberchat.com/astro-visualization?utm_source=ios_app", "_blank"); } catch { /* ignore */ } }}
              className="mt-3 btn-primary rounded-full px-5 py-2 text-xs font-medium"
              data-testid="av-ios-open-web-btn"
            >
              Open on astrokuberchat.com
            </button>
          </div>
        ) : (
          <div className="mt-6 space-y-3">
            <div className="rounded-2xl bg-amber-50 border border-amber-200 p-4 text-sm text-[#3F2D1B]" data-testid="av-price-breakdown">
              <div className="flex justify-between"><span>Astro Visualization · Lifetime for this chart</span><span>{formatPrice ? formatPrice(unlockPrice) : `₹${unlockPrice}`}</span></div>
              <div className="flex justify-between text-muted"><span>{taxLabel}</span><span>{formatPrice ? formatPrice(gstAmount) : `₹${gstAmount}`}</span></div>
              <div className="border-t border-amber-300/60 mt-2 pt-2 flex justify-between font-medium"><span>Total</span><span>{formatPrice ? formatPrice(totalWithGst) : `₹${totalWithGst}`}</span></div>
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                disabled={paying || !canPayWithWallet}
                onClick={doWallet}
                className="rounded-full px-5 py-2.5 text-sm font-medium border border-amber-400 text-amber-800 bg-white hover:bg-amber-50 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
                data-testid="av-wallet-btn"
              >
                <Wallet size={16} weight="duotone" />
                Pay from wallet · Balance {formatPrice ? formatPrice(walletBalance) : `₹${walletBalance}`}
              </button>
              <button
                disabled={paying || unlockLoading}
                onClick={doRazorpay}
                className="rounded-full px-6 py-2.5 text-sm font-medium bg-[#3F2D1B] text-white hover:bg-[#5a3e22] disabled:opacity-40 inline-flex items-center gap-2"
                data-testid="av-razorpay-btn"
              >
                <LockKey size={16} weight="fill" />
                {paying ? "Opening secure payment…" : `Unlock for ${formatPrice ? formatPrice(totalWithGst) : `₹${totalWithGst}`}`}
              </button>
            </div>
            <div className="text-[11px] text-muted">
              Payments powered by Razorpay · UPI, cards, netbanking, wallets all supported.
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

// ----------------------------------------------------------------------------
// UNLOCKED VIEW — the cinematic scene
// ----------------------------------------------------------------------------
function UnlockedView({
  journey, journeyBusy, allSignposts, selectedIndex, setSelectedIndex,
  selectedFutureIndex, setSelectedFutureIndex,
  activeSign, activePalette, revealFuture, showFuture, futureData, futureBusy, name, isSuperAdmin,
  form, setForm,
}) {
  const hasBirth = !!(form.date && form.time && form.place);
  if (isSuperAdmin && !hasBirth) {
    return (
      <div className="rounded-3xl border border-amber-200 bg-white p-6 max-w-2xl mx-auto" data-testid="av-superadmin-bd-panel">
        <div className="label-eyebrow text-gold flex items-center gap-2"><Crown size={14} weight="duotone" /> Super Admin · free access</div>
        <h2 className="font-serif-display text-2xl text-[#3F2D1B] mt-2">Enter birth details to preview</h2>
        <p className="text-muted text-sm mt-1">You're already unlocked. Fill birth details to render the journey.</p>
        <div className="mt-5 grid sm:grid-cols-2 gap-3">
          <input type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} className="px-3 py-2.5 rounded-md border border-soft bg-bg" data-testid="av-sa-date" />
          <input type="time" value={form.time} onChange={(e) => setForm((f) => ({ ...f, time: e.target.value }))} className="px-3 py-2.5 rounded-md border border-soft bg-bg" data-testid="av-sa-time" />
          <div className="sm:col-span-2">
            <PlaceAutocompleteInput
              value={form.place}
              onChange={(v) => setForm((f) => ({ ...f, place: v }))}
              onPick={({ name, timezone }) => setForm((f) => ({ ...f, place: name, timezone: timezone || f.timezone }))}
              placeholder="Mumbai, India"
              testId="av-sa-place"
            />
          </div>
        </div>
      </div>
    );
  }

  if (journeyBusy || !journey) {
    return (
      <div className="min-h-[400px] flex items-center justify-center" data-testid="av-loading">
        <div className="text-center">
          <div className="inline-block w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full animate-spin" />
          <div className="mt-3 text-sm text-muted">Casting your life-journey through the wheel of time…</div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="av-unlocked-view">
      {/* Birth-details bar — always visible so users can change chart & re-compute */}
      <BirthDetailsBar form={form} setForm={setForm} />

      {/* Game HUD — top status bar */}
      {activeSign && (
        <GameHUD
          level={selectedIndex != null ? (selectedIndex + 1) : (allSignposts.length || 1)}
          totalLevels={allSignposts.length}
          planet={activeSign.planet}
          palette={activePalette}
          currentAge={journey.current_age}
          journey={journey}
        />
      )}

      {/* CINEMATIC SCENE (contains its own header + current mahadasha panel) */}
      <JourneyScene
        allSignposts={allSignposts}
        selectedIndex={selectedIndex}
        setSelectedIndex={setSelectedIndex}
        activeSign={activeSign}
        activePalette={activePalette}
        journey={journey}
        name={name}
      />

      {/* MAIN HIGHLIGHT CARD */}
      {activeSign && (
        <DetailCard signpost={activeSign} palette={activePalette} />
      )}

      {/* PLANET TIMELINE STRIP */}
      <PlanetTimelineStrip
        signposts={allSignposts}
        selectedIndex={selectedIndex}
        setSelectedIndex={setSelectedIndex}
        futureData={futureData}
      />

      {/* FUTURE ARC */}
      <FutureSection
        showFuture={showFuture}
        futureData={futureData}
        futureBusy={futureBusy}
        revealFuture={revealFuture}
        selectedFutureIndex={selectedFutureIndex}
        setSelectedFutureIndex={setSelectedFutureIndex}
      />
    </div>
  );
}

// ----------------------------------------------------------------------------
// Journey header (title + current mahadasha panel on top-right)
// ----------------------------------------------------------------------------
function JourneyHeader({ journey, activePalette, name }) {
  const cur = journey.current;
  const curPal = cur ? PALETTES[cur.palette] : activePalette;
  return (
    <div className="flex items-start justify-between flex-wrap gap-4 mb-4">
      <div>
        <h1 className="font-serif-display text-3xl sm:text-4xl text-[#3F2D1B] leading-tight" data-testid="av-title">
          Your Life Journey <span className="text-amber-700 italic">— Past to Present</span>
        </h1>
        <p className="text-muted text-sm mt-1 max-w-lg">
          {name}'s road, each mahadasha a lit signpost. Tap any phase to change the scene.
        </p>
      </div>
      {cur && (
        <div
          className="rounded-2xl px-4 py-3 border shadow-lg text-left"
          style={{
            background: `linear-gradient(140deg, ${curPal.tintFrom} 0%, ${curPal.tintTo} 130%)`,
            borderColor: curPal.ring + "aa",
            color: curPal.ink,
          }}
          data-testid="av-current-panel"
        >
          <div className="text-[10px] uppercase tracking-widest opacity-80" style={{ color: curPal.accent }}>
            Current Mahadasha
          </div>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-2xl" style={{ color: curPal.accent }}>{curPal.glyph}</span>
            <span className="font-serif-display text-2xl">{cur.planet}</span>
          </div>
          <div className="text-xs opacity-80">{cur.from.slice(0,4)} – {cur.to.slice(0,4)}</div>
          <div className="text-[11px] mt-1" style={{ color: curPal.accent }}>{cur.keywords.slice(0,3).join(" • ")}</div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// GAME LAYER — sound, HUD, cinematic transitions
// ============================================================================

/* Tiny Web-Audio "beep-gong" — no assets, no libraries. */
function playGameSound(type = "click") {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    if (!window.__avAudioCtx) window.__avAudioCtx = new AC();
    const ctx = window.__avAudioCtx;
    if (ctx.state === "suspended") ctx.resume();
    const now = ctx.currentTime;
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.connect(g); g.connect(ctx.destination);
    if (type === "click") { o.frequency.value = 720; g.gain.setValueAtTime(0.18, now); g.gain.exponentialRampToValueAtTime(0.001, now + 0.14); o.start(now); o.stop(now + 0.15); }
    else if (type === "reveal") {
      // gentle gong: three descending overtones
      [523.25, 392.0, 261.63].forEach((f, i) => {
        const os = ctx.createOscillator(); const gs = ctx.createGain();
        os.connect(gs); gs.connect(ctx.destination);
        os.frequency.value = f;
        gs.gain.setValueAtTime(0.10, now + i * 0.06);
        gs.gain.exponentialRampToValueAtTime(0.001, now + 0.9 + i * 0.06);
        os.start(now + i * 0.06); os.stop(now + 1.1 + i * 0.06);
      });
    } else if (type === "unlock") {
      // ascending arpeggio
      [261.63, 329.63, 392.0, 523.25].forEach((f, i) => {
        const os = ctx.createOscillator(); const gs = ctx.createGain();
        os.connect(gs); gs.connect(ctx.destination);
        os.type = "triangle"; os.frequency.value = f;
        gs.gain.setValueAtTime(0.14, now + i * 0.08);
        gs.gain.exponentialRampToValueAtTime(0.001, now + 0.35 + i * 0.08);
        os.start(now + i * 0.08); os.stop(now + 0.4 + i * 0.08);
      });
    }
  } catch (_) { /* ignore audio errors */ }
}

/* Cinematic transition overlay — big centered planet glyph flash + particles. */
function CinematicTransition({ palette, planet, onDone }) {
  const rootRef = useRef(null);
  useEffect(() => {
    if (!rootRef.current) return;
    const glyph = rootRef.current.querySelector("[data-glyph]");
    const halo = rootRef.current.querySelector("[data-halo]");
    const flash = rootRef.current.querySelector("[data-flash]");
    const banner = rootRef.current.querySelector("[data-banner]");
    // Screen flash
    anime({ targets: flash, opacity: [0, 0.85, 0], duration: 600, easing: "easeOutExpo" });
    // Glyph pop
    anime({ targets: glyph, opacity: [0, 1, 1, 0], scale: [0.4, 1.4, 1.0, 0.9], rotate: ["-8deg", "8deg", "0deg"], duration: 1500, easing: "easeOutBack" });
    anime({ targets: halo, opacity: [0, 0.85, 0], scale: [0.6, 2.4], duration: 1500, easing: "easeOutExpo" });
    anime({ targets: banner, opacity: [0, 1, 1, 0], translateY: [30, 0, 0, -20], duration: 1500, easing: "easeOutCubic", complete: () => onDone?.() });
    // Particles
    const parts = rootRef.current.querySelectorAll("[data-p]");
    anime({
      targets: parts,
      translateX: () => anime.random(-380, 380),
      translateY: () => anime.random(-260, 260),
      scale: () => anime.random(0.4, 1.6),
      opacity: [1, 0],
      duration: 1300,
      easing: "easeOutExpo",
    });
    playGameSound("reveal");
  }, [planet, onDone]);
  return (
    <div ref={rootRef} className="fixed inset-0 z-[999] pointer-events-none flex items-center justify-center">
      <div data-flash className="absolute inset-0" style={{ background: `radial-gradient(circle at 50% 50%, ${palette.accent}dd 0%, transparent 65%)`, opacity: 0 }} />
      <div className="relative">
        <div data-halo className="absolute inset-0 rounded-full" style={{ width: 220, height: 220, marginLeft: -110, marginTop: -110, background: `radial-gradient(circle, ${palette.accent}aa 0%, transparent 70%)`, opacity: 0 }} />
        <div data-glyph className="relative text-[10rem] font-serif-display leading-none select-none" style={{ color: palette.accent, textShadow: `0 0 60px ${palette.accent}, 0 0 20px #000` , opacity: 0 }}>
          {palette.glyph}
        </div>
        <div data-banner className="mt-2 text-center text-sm uppercase tracking-[0.3em] font-medium" style={{ color: palette.accent, textShadow: "0 2px 8px rgba(0,0,0,0.9)", opacity: 0 }}>
          {planet} · Level Unlocked
        </div>
        {Array.from({ length: 24 }).map((_, i) => (
          <div key={i} data-p className="absolute left-1/2 top-1/2 w-2 h-2 rounded-full" style={{ background: palette.accent, boxShadow: `0 0 6px ${palette.accent}` }} />
        ))}
      </div>
    </div>
  );
}

/* GameHUD — sticky top bar with current level / age / progress. */
function GameHUD({ level, totalLevels, planet, palette, currentAge, journey }) {
  const lifeProgress = Math.min(1, currentAge / 84); // 84 = typical Vimshottari span reference
  return (
    <div className="mb-4 rounded-2xl border border-amber-500/30 bg-gradient-to-r from-[#0b0f1f] via-[#1a1a2e] to-[#0b0f1f] shadow-lg overflow-hidden" data-testid="av-game-hud">
      <div className="grid grid-cols-3 gap-2 p-3 sm:p-4 items-center">
        <div className="min-w-0">
          <div className="text-[9px] uppercase tracking-widest text-amber-400/70">Current Level</div>
          <div className="flex items-baseline gap-1.5 mt-0.5">
            <span className="text-xl font-bold" style={{ color: palette.accent }}>{palette.glyph}</span>
            <span className="font-serif-display text-lg text-amber-50 truncate">{planet}</span>
          </div>
          <div className="text-[10px] text-amber-100/60 mt-0.5">Stage {level} / {totalLevels}</div>
        </div>
        <div className="text-center">
          <div className="text-[9px] uppercase tracking-widest text-amber-400/70">XP · Years Lived</div>
          <div className="font-serif-display text-2xl text-amber-50 mt-0.5">{currentAge.toFixed(1)}</div>
          <div className="text-[10px] text-amber-100/60">of 84 total</div>
        </div>
        <div className="min-w-0 text-right">
          <div className="text-[9px] uppercase tracking-widest text-amber-400/70">Life Progress</div>
          <div className="mt-1.5 h-2 rounded-full bg-black/50 overflow-hidden">
            <div className="h-full rounded-full transition-all duration-1000" style={{ width: `${lifeProgress * 100}%`, background: `linear-gradient(90deg, ${palette.ring}, ${palette.accent})`, boxShadow: `0 0 8px ${palette.accent}` }} />
          </div>
          <div className="text-[10px] text-amber-100/60 mt-0.5">{Math.round(lifeProgress * 100)}% complete</div>
        </div>
      </div>
    </div>
  );
}


// ----------------------------------------------------------------------------
// The cinematic scene — the star of the show
// ----------------------------------------------------------------------------
function JourneyScene({ allSignposts, selectedIndex, setSelectedIndex, activeSign, activePalette, journey, name }) {
  const sceneRef = useRef(null);
  const videoRef = useRef(null);
  const [videoPaused, setVideoPaused] = useState(true);
  const [transition, setTransition] = useState(null); // {palette, planet}
  const overlayRefs = useRef([]);
  overlayRefs.current = [];
  const addOverlay = (el) => { if (el && !overlayRefs.current.includes(el)) overlayRefs.current.push(el); };

  // Trigger cinematic transition + sound on any dasha click
  const triggerDashaSelect = (i, isFuture = false) => {
    const sp = allSignposts[i];
    if (!sp) return;
    const pal = PALETTES[sp.palette] || PALETTES.jupiter;
    setTransition({ palette: pal, planet: sp.planet, key: `${i}-${Date.now()}` });
    // Fire sound
    playGameSound(isFuture ? "unlock" : "click");
    // Camera "shake" — tiny scale/translate pulse on scene
    if (sceneRef.current) {
      anime({
        targets: sceneRef.current,
        scale: [1, 1.015, 1],
        translateX: [0, isFuture ? 2 : 1, 0],
        translateY: [0, isFuture ? -1 : 0, 0],
        duration: 380,
        easing: "easeInOutSine",
      });
    }
    setSelectedIndex(i);
  };

  // Base scene image aspect ratio (portrait). The generated Nano-Banana scene
  // is 864x1296 → w/h = 0.6667.
  const SCENE_IMG = "/astro-viz/scene-with-characters.png";

  // Fixed anchor positions on the base image, aligned to the FOUR personas in
  // scene-with-characters.png. Each anchor now carries the AGE BRACKET it
  // represents so we can map each Mahadasha to the correct persona by age,
  // not by chronological order — this is the key fix.
  //
  // Character head positions (measured on the 848×1256 base image):
  //   kid  head → (32%, 60%) — small figure with red backpack, far up the hill
  //   teen head → (47%, 64%) — mid-back, blue jacket
  //   adult head→ (63%, 66%) — man in suit
  //   elder head→ (40%, 77%) — foreground, with cane
  //
  // Signpost card is centred horizontally on the character (translateX(-50%))
  // and floats above with a hair-thin wire dropping to a pin at the character.
  const ANCHORS = [
    {
      bucket: "kid",   ageStart: 0,  ageEnd: 12,
      char:  { top: "60%", left: "32%" },   // character head
      card:  { top: "32%", left: "22%" },   // signpost card (upper-left)
      label: { top: "63%", left: "32%" },   // handwritten stage label near character
      labelText: "Little Kid",
      labelTilt: -6,
    },
    {
      bucket: "teen",  ageStart: 12, ageEnd: 19,
      char:  { top: "64%", left: "47%" },
      card:  { top: "40%", left: "47%" },
      label: { top: "68%", left: "47%" },
      labelText: "Young Boy",
      labelTilt: -4,
    },
    {
      bucket: "adult", ageStart: 19, ageEnd: 55,
      char:  { top: "66%", left: "63%" },
      card:  { top: "42%", left: "72%" },
      label: { top: "70%", left: "63%" },
      labelText: "Adult",
      labelTilt: -3,
    },
    {
      bucket: "elder", ageStart: 55, ageEnd: 200,
      char:  { top: "76%", left: "40%" },
      card:  { top: "56%", left: "12%" },
      label: { top: "88%", left: "40%" },
      labelText: "Elder",
      labelTilt: -5,
    },
  ];

  // Assign each anchor the Mahadasha that best fits it. Algorithm:
  //   1. Iterate anchors in life-stage order (kid → elder).
  //   2. For each anchor, pick the UN-USED MD with the best score:
  //         score = age-window overlap (in years) + 100 if MD's natural
  //                 bucket === this anchor's bucket.
  //   3. Once an MD is assigned to an anchor, it can't be used again — so
  //      the same MD never appears twice.
  //   4. Anchors that don't have any overlapping MD stay empty (their
  //      character label still shows so the persona is visible).
  const anchorAssignments = (() => {
    const used = new Set();
    return ANCHORS.map((anchor) => {
      let best = null;
      let bestScore = 0;
      allSignposts.forEach((sp, idx) => {
        if (used.has(idx)) return;
        const overlap = Math.max(
          0,
          Math.min(sp.to_age ?? 0, anchor.ageEnd) - Math.max(sp.from_age ?? 0, anchor.ageStart),
        );
        if (overlap <= 0) return;
        const bucketMatchBonus = sp.stage?.bucket === anchor.bucket ? 100 : 0;
        const score = overlap + bucketMatchBonus;
        if (score > bestScore) { bestScore = score; best = { sp, index: idx }; }
      });
      if (best) used.add(best.index);
      return { anchor, assignment: best };
    });
  })();

  // Palette-driven scene tint (very subtle — the base image is the star).
  const tintStyle = {
    background: `linear-gradient(180deg, transparent 0%, ${activePalette.tintFrom}22 60%, ${activePalette.tintFrom}55 100%)`,
    transition: "background 900ms ease-in-out",
    mixBlendMode: "multiply",
  };

  // anime.js entry animation
  useEffect(() => {
    if (!sceneRef.current) return;
    anime({
      targets: overlayRefs.current,
      opacity: [0, 1],
      translateY: [-10, 0],
      scale: [0.9, 1],
      delay: anime.stagger(150, { start: 200 }),
      duration: 550,
      easing: "easeOutBack",
    });
  }, [allSignposts.length]);

  // Scroll-driven reveal: overlays slide-in as the user scrolls INTO the scene.
  useEffect(() => {
    if (!sceneRef.current) return;
    const el = sceneRef.current;
    const onScroll = () => {
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight;
      const progress = Math.min(1, Math.max(0, 1 - (rect.top - vh * 0.15) / (rect.height + vh * 0.2)));
      el.style.setProperty("--scroll-progress", progress.toFixed(3));
      // As user scrolls, each overlay reveals in sequence
      overlayRefs.current.forEach((ov, i) => {
        const threshold = (i + 0.5) / overlayRefs.current.length * 0.85 + 0.05;
        if (progress >= threshold && ov.dataset.revealed !== "1") {
          ov.dataset.revealed = "1";
          anime({
            targets: ov,
            opacity: [0, 1],
            translateY: [-10, 0],
            scale: [0.85, 1],
            duration: 550,
            easing: "easeOutBack",
          });
        }
      });
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [allSignposts.length]);

  if (allSignposts.length === 0) return null;

  return (
    <>
    {transition && (
      <CinematicTransition
        key={transition.key}
        palette={transition.palette}
        planet={transition.planet}
        onDone={() => setTransition(null)}
      />
    )}
    <div
      ref={sceneRef}
      className="relative mx-auto overflow-hidden rounded-3xl shadow-2xl border border-white/10"
      style={{
        width: "100%",
        maxWidth: 720,
        aspectRatio: "864 / 1296",
        background: "#050914",
      }}
      data-testid="av-scene"
    >
      {/* Photorealistic base scene: video overlays the still image; still shows until video plays */}
      <img
        src={SCENE_IMG}
        alt=""
        className="absolute inset-0 w-full h-full object-cover select-none pointer-events-none"
        draggable={false}
      />
      <video
        key="scene-video"
        ref={videoRef}
        poster={SCENE_IMG}
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        style={{ minWidth: "100%", minHeight: "100%" }}
        className="absolute inset-0 w-full h-full object-cover select-none pointer-events-none"
        onLoadedData={(e) => {
          const v = e.currentTarget;
          v.play().then(() => setVideoPaused(false)).catch(() => setVideoPaused(true));
        }}
        onPlay={() => setVideoPaused(false)}
        onPause={() => setVideoPaused(true)}
        onError={(e) => {
          // eslint-disable-next-line no-console
          console.warn("[astro-viz] scene video failed to load; falling back to still image");
          e.currentTarget.style.display = "none";
        }}
        data-testid="av-scene-video"
      >
        <source src="/astro-viz/scene.mp4" type="video/mp4" />
      </video>

      {/* Manual play button — appears if browser blocked autoplay */}
      {videoPaused && (
        <button
          type="button"
          onClick={() => {
            videoRef.current?.play().then(() => setVideoPaused(false)).catch(() => {});
          }}
          className="absolute z-30 bottom-3 left-3 inline-flex items-center gap-1.5 rounded-full backdrop-blur-md border border-white/25 bg-black/60 px-3 py-1.5 text-[11px] text-white hover:bg-black/75 transition-colors"
          data-testid="av-scene-play-btn"
        >
          <span className="text-amber-300">▶</span> Play cinematic
        </button>
      )}
      {/* Palette tint overlay (very subtle) */}
      <div className="absolute inset-0 pointer-events-none" style={tintStyle} />
      {/* Subtle vignette */}
      <div className="absolute inset-0 pointer-events-none"
           style={{ background: "radial-gradient(ellipse at 50% 45%, transparent 45%, rgba(0,0,0,0.25) 100%)" }} />

      {/* Signposts — each floats above its matching character with a wire
          dropping to a pin at the character's head. Assignment is by AGE
          BUCKET, not by chronological order. */}
      {anchorAssignments.map(({ anchor, assignment }, i) => {
        if (!assignment) return null;
        const sp = assignment.sp;
        const globalIndex = assignment.index;
        const pal = PALETTES[sp.palette] || PALETTES.jupiter;
        const isActive = selectedIndex === globalIndex;
        const isCurrent = !!sp._isCurrent;

        // Compute wire from card-bottom to character-head. We render an SVG
        // line between the two absolute positions (both expressed in %).
        const cardX = parseFloat(anchor.card.left);
        const cardY = parseFloat(anchor.card.top);
        const charX = parseFloat(anchor.char.left);
        const charY = parseFloat(anchor.char.top);

        return (
          <React.Fragment key={`sign-${i}`}>
            {/* Thin wire from card to character head */}
            <svg
              className="absolute inset-0 z-[8] pointer-events-none"
              width="100%" height="100%"
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
              aria-hidden
            >
              <line
                x1={cardX} y1={cardY + 3}
                x2={charX} y2={charY - 2}
                stroke={isCurrent ? "#4ade80" : pal.accent}
                strokeOpacity="0.55"
                strokeWidth="0.18"
                strokeDasharray="0.6 0.6"
                vectorEffect="non-scaling-stroke"
              />
            </svg>

            {/* Pin dot at character head */}
            <div
              className="absolute z-[9] pointer-events-none"
              style={{
                top: anchor.char.top, left: anchor.char.left,
                transform: "translate(-50%, -50%)",
              }}
              aria-hidden
            >
              <div className="w-2.5 h-2.5 rounded-full ring-2 ring-black/50"
                   style={{
                     background: isCurrent ? "#4ade80" : pal.accent,
                     boxShadow: `0 0 10px ${isCurrent ? "#4ade80" : pal.accent}`,
                   }} />
            </div>

            {/* Signpost card — sits ABOVE the character, centered on card.left */}
            <button
              ref={addOverlay}
              onClick={() => triggerDashaSelect(globalIndex)}
              className="absolute z-10 text-left transition-transform hover:scale-[1.03]"
              style={{
                top: anchor.card.top, left: anchor.card.left,
                transform: "translateX(-50%)",
                opacity: 0,
                minWidth: 108, maxWidth: 180,
              }}
              data-testid={`av-signpost-${i}`}
              data-bucket={anchor.bucket}
            >
              <div
                className="rounded-lg border backdrop-blur-md text-center px-2 py-1.5"
                style={{
                  background: "rgba(15,15,20,0.78)",
                  borderColor: isCurrent ? "#4ade80" : pal.ring,
                  borderWidth: isActive || isCurrent ? 2 : 1,
                  boxShadow: isActive || isCurrent ? `0 0 16px ${pal.accent}66` : "0 4px 10px rgba(0,0,0,0.4)",
                  color: pal.ink,
                }}
              >
                <div className="text-[10px] font-semibold uppercase tracking-widest"
                     style={{ color: pal.accent, opacity: 0.85 }}>
                  {anchor.labelText} · Age {sp.stage?.age_band || `${Math.round(sp.from_age)}–${Math.round(sp.to_age)}`}
                </div>
                <div className="text-[11px] font-bold tracking-wide leading-tight mt-0.5"
                     style={{ color: pal.accent }}>
                  {sp.planet} Mahadasha
                </div>
                <div className="text-[10px] text-white/85 mt-0.5">
                  {`${sp.from.slice(0, 4)} — ${sp.to.slice(0, 4)}`}
                </div>
                <div className="text-[9px] text-white/60 mt-0.5 leading-snug">
                  {sp.keywords.slice(0, 2).join(" • ")}
                </div>
              </div>
            </button>
          </React.Fragment>
        );
      })}

      {/* Handwritten stage labels — one per persona, positioned right at the
          character so it's visually unambiguous which figure the label refers to. */}
      {ANCHORS.map((anchor, i) => (
        <div
          key={`label-${i}`}
          ref={addOverlay}
          className="absolute z-10 pointer-events-none"
          style={{
            top: anchor.label.top, left: anchor.label.left,
            transform: `translateX(-50%) rotate(${anchor.labelTilt || -4}deg)`,
            opacity: 0,
          }}
          data-testid={`av-stage-label-${i}`}
          data-bucket={anchor.bucket}
        >
          <div className="text-white italic drop-shadow-lg font-serif-display text-sm sm:text-base whitespace-nowrap"
               style={{ textShadow: "0 2px 6px rgba(0,0,0,0.9), 0 0 12px rgba(0,0,0,0.6)" }}>
            {anchor.labelText}
          </div>
        </div>
      ))}

      {/* Header inside scene: Title + Current Mahadasha panel */}
      <div className="absolute inset-x-0 top-0 z-10 p-3 sm:p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="max-w-[55%]">
            <h1 className="text-white text-lg sm:text-xl font-serif-display leading-tight drop-shadow-lg" data-testid="av-title">
              Your Life Journey <br />
              <span className="text-amber-300 italic">— Past to Present</span>
            </h1>
            <p className="text-white/75 text-[11px] sm:text-xs mt-1 max-w-[16em] drop-shadow">
              Explore your life journey through Mahadashas. Tap on any phase to see key events and highlights.
            </p>
          </div>
          {journey.current && (
            <CurrentMahadashaTile current={journey.current} refPush={addOverlay} />
          )}
        </div>
      </div>
    </div>
    </>
  );
}

function CurrentMahadashaTile({ current, refPush }) {
  const pal = PALETTES[current.palette] || PALETTES.jupiter;
  return (
    <div
      ref={refPush}
      className="rounded-xl px-3 py-2 border shadow-xl backdrop-blur-md text-right"
      style={{
        background: `linear-gradient(135deg, ${pal.tintFrom}dd 0%, ${pal.tintTo}66 100%)`,
        borderColor: pal.ring + "aa",
        color: pal.ink,
        opacity: 0,
        minWidth: 130,
      }}
      data-testid="av-current-panel"
    >
      <div className="text-[9px] uppercase tracking-widest opacity-90" style={{ color: pal.accent }}>
        Current Mahadasha
      </div>
      <div className="flex items-center gap-1.5 justify-end mt-0.5">
        <span className="text-lg leading-none" style={{ color: pal.accent }}>{pal.glyph}</span>
        <span className="font-serif-display text-lg leading-none">{current.planet}</span>
      </div>
      <div className="text-[10px] opacity-90">{current.from.slice(0,4)} – {current.to.slice(0,4)}</div>
      <div className="text-[9px] mt-0.5" style={{ color: pal.accent }}>
        {current.keywords.slice(0,3).join(" • ")}
      </div>
    </div>
  );
}


// ----------------------------------------------------------------------------
// Bottom planet timeline strip (matches screenshot)
// ----------------------------------------------------------------------------
function PlanetTimelineStrip({ signposts, selectedIndex, setSelectedIndex, futureData }) {
  const stripRef = useRef(null);
  useEffect(() => {
    if (!stripRef.current) return;
    anime({
      targets: stripRef.current.querySelectorAll("[data-planet-chip]"),
      opacity: [0, 1],
      translateY: [12, 0],
      duration: 500,
      delay: anime.stagger(60),
      easing: "easeOutQuad",
    });
  }, [signposts]);
  return (
    <div
      ref={stripRef}
      className="mt-6 rounded-2xl border border-white/10 bg-[#0b0f1f]/95 p-4 shadow-inner"
      data-testid="av-timeline-strip"
    >
      <div className="relative">
        {/* horizontal line */}
        <div className="absolute left-4 right-4 top-1/2 h-[2px] bg-gradient-to-r from-transparent via-amber-500/60 to-transparent" />
        <div className="relative flex items-center justify-between gap-2 overflow-x-auto">
          {signposts.map((sp, i) => {
            const pal = PALETTES[sp.palette] || PALETTES.jupiter;
            const isActive = selectedIndex === i;
            return (
              <button
                key={i}
                data-planet-chip
                onClick={() => setSelectedIndex(i)}
                className="relative flex flex-col items-center shrink-0 mx-1 group"
                data-testid={`av-timeline-chip-${i}`}
              >
                <div
                  className={`w-11 h-11 rounded-full flex items-center justify-center text-xl border-2 transition-all ${isActive ? "scale-110" : ""}`}
                  style={{
                    borderColor: sp._isCurrent ? "#4ade80" : pal.ring,
                    background: isActive ? pal.tintTo : "#0b0f1f",
                    color: pal.accent,
                    boxShadow: isActive || sp._isCurrent ? `0 0 24px ${pal.accent}55` : undefined,
                  }}
                >
                  {pal.glyph}
                </div>
                <div className="text-[10px] mt-1 text-white/70">{sp.from.slice(0, 4)}</div>
              </button>
            );
          })}
          {/* Future tail */}
          <div className="flex flex-col items-center shrink-0 mx-1 opacity-60">
            <div className="w-11 h-11 rounded-full border-2 border-dashed border-white/25 flex items-center justify-center text-xs text-white/60">
              +N
            </div>
            <div className="text-[10px] mt-1 text-white/50">Future</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Main Highlight card
// ----------------------------------------------------------------------------
function DetailCard({ signpost, palette }) {
  const cardRef = useRef(null);
  useEffect(() => {
    if (!cardRef.current) return;
    anime({
      targets: cardRef.current,
      opacity: [0, 1],
      translateY: [16, 0],
      duration: 500,
      easing: "easeOutCubic",
    });
    anime({
      targets: cardRef.current.querySelectorAll("[data-event-row]"),
      opacity: [0, 1],
      translateX: [-12, 0],
      duration: 400,
      delay: anime.stagger(70, { start: 200 }),
      easing: "easeOutCubic",
    });
  }, [signpost]);

  return (
    <div
      ref={cardRef}
      className="mt-6 rounded-3xl shadow-lg border overflow-hidden"
      style={{
        background: `linear-gradient(140deg, ${palette.tintFrom}f2 0%, #0b0f1f 100%)`,
        borderColor: palette.ring + "66",
        color: palette.ink,
      }}
      data-testid="av-detail-card"
    >
      <div className="grid md:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)] gap-0">
        {/* LEFT: content */}
        <div className="p-6 sm:p-8">
          {signpost._isFuture && (
            <div className="mb-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border"
                 style={{ borderColor: palette.ring, color: palette.accent, background: "rgba(0,0,0,0.35)" }}>
              <FastForward size={10} weight="fill" /> Future preview
            </div>
          )}
          <div className="flex items-baseline justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div
                className="w-11 h-11 rounded-full flex items-center justify-center text-2xl border"
                style={{ borderColor: palette.ring, background: "#000", color: palette.accent }}
              >
                {palette.glyph}
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest opacity-80" style={{ color: palette.accent }}>
                  Main Highlight · Ages {signpost.stage.age_band}
                </div>
                <div className="font-serif-display text-2xl sm:text-3xl mt-0.5" style={{ color: palette.accent }}>
                  {signpost.planet} Mahadasha
                </div>
                <div className="text-xs opacity-70">
                  {signpost.from} → {signpost.to} · {signpost.years} years
                </div>
              </div>
            </div>
          </div>

          <div className="mt-4">
            <div className="text-[10px] uppercase tracking-widest opacity-70 mb-1">Keywords</div>
            <div className="text-sm">{signpost.keywords.join(" · ")}</div>
          </div>

          <div className="mt-5 rounded-xl bg-black/40 border border-white/10 p-4">
            <div className="text-[10px] uppercase tracking-widest opacity-70 mb-1">Your placement</div>
            <div className="text-sm">{signpost.placement}</div>
            <div className="text-xs mt-2 opacity-80 italic">{signpost.tagline}</div>
          </div>

          <div className="mt-5">
            <div className="text-[10px] uppercase tracking-widest opacity-70 mb-2 flex items-center gap-1">
              <Lightning size={12} weight="fill" /> Key events
            </div>
            <div className="space-y-2">
              {signpost.key_events.map((e, i) => (
                <div key={i} data-event-row className="flex items-start gap-3 rounded-xl bg-black/40 border border-white/10 p-3" data-testid={`av-event-${i}`}>
                  <CheckCircle size={18} weight="fill" style={{ color: palette.accent, flexShrink: 0 }} />
                  <div className="flex-1">
                    <div className="flex items-baseline justify-between gap-2">
                      <div className="text-sm font-medium" style={{ color: palette.accent }}>
                        {signpost.planet}–{e.sub_lord}
                      </div>
                      <div className="text-[10px] opacity-70">Age {e.age}</div>
                    </div>
                    <div className="text-sm mt-0.5 leading-snug">{e.event}</div>
                    <div className="text-[10px] mt-0.5 opacity-60">{e.from} → {e.to}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* RIGHT: planet-themed cinematic image (below on mobile) */}
        <div className="relative min-h-[280px] md:min-h-full overflow-hidden order-first md:order-none">
          <img
            src={`/astro-viz/planets/${signpost.palette}.png`}
            alt={`${signpost.planet} — themed visual`}
            className="absolute inset-0 w-full h-full object-cover"
            draggable={false}
            data-testid="av-detail-planet-img"
          />
          {/* Gradient fade to blend into card on the left edge (only on desktop) */}
          <div className="hidden md:block absolute inset-y-0 left-0 w-24 pointer-events-none"
               style={{ background: `linear-gradient(90deg, ${palette.tintFrom}f2, transparent)` }} />
          {/* Bottom caption chip */}
          <div className="absolute bottom-3 right-3 rounded-full px-3 py-1 backdrop-blur-md border text-[10px] uppercase tracking-widest"
               style={{ background: "rgba(0,0,0,0.55)", borderColor: palette.ring + "aa", color: palette.accent }}>
            {palette.name} · {signpost.keywords[0]}
          </div>
        </div>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Birth-details bar — collapsible, sits above the scene, lets user re-run
// the journey with different DOB/time/place without leaving the page.
// ----------------------------------------------------------------------------
function BirthDetailsBar({ form, setForm }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mb-4 rounded-2xl border border-amber-200/60 bg-white/85 backdrop-blur shadow-sm" data-testid="av-birth-bar">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-2.5 text-left"
        data-testid="av-birth-bar-toggle"
      >
        <div className="flex items-center gap-2 text-sm text-[#3F2D1B]">
          <Calendar size={16} weight="duotone" className="text-amber-700" />
          <span className="font-medium">Birth details:</span>
          <span className="text-muted truncate">
            {form.date || "—"} · {form.time || "—:—"} · {form.place || "no place"}
          </span>
        </div>
        <span className="text-amber-700 text-xs font-medium">{open ? "Close" : "Change"}</span>
      </button>
      {open && (
        <div className="border-t border-amber-200/60 p-4 grid sm:grid-cols-2 gap-3">
          <label className="block">
            <span className="label-eyebrow text-xs">Date of birth</span>
            <input
              type="date" required value={form.date}
              onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
              className="mt-1.5 w-full px-3 py-2 rounded-md border border-soft bg-bg text-sm"
              data-testid="av-birth-bar-date"
            />
          </label>
          <label className="block">
            <span className="label-eyebrow text-xs">Time of birth</span>
            <input
              type="time" required value={form.time}
              onChange={(e) => setForm((f) => ({ ...f, time: e.target.value }))}
              className="mt-1.5 w-full px-3 py-2 rounded-md border border-soft bg-bg text-sm"
              data-testid="av-birth-bar-time"
            />
          </label>
          <label className="block sm:col-span-2">
            <span className="label-eyebrow text-xs">Place of birth</span>
            <div className="mt-1.5">
              <PlaceAutocompleteInput
                value={form.place}
                onChange={(v) => setForm((f) => ({ ...f, place: v }))}
                onPick={({ name, timezone }) => setForm((f) => ({ ...f, place: name, timezone: timezone || f.timezone }))}
                placeholder="Mumbai, India"
                testId="av-birth-bar-place"
              />
            </div>
          </label>
          <div className="sm:col-span-2 text-[11px] text-muted italic">
            Change any field — your journey will re-compute automatically.
          </div>
        </div>
      )}
    </div>
  );
}


// ----------------------------------------------------------------------------
// Future arc
// ----------------------------------------------------------------------------
function FutureSection({ showFuture, futureData, futureBusy, revealFuture, selectedFutureIndex, setSelectedFutureIndex }) {
  const containerRef = useRef(null);
  useEffect(() => {
    if (!showFuture || !containerRef.current) return;
    anime({
      targets: containerRef.current.querySelectorAll("[data-future-card]"),
      opacity: [0, 1],
      translateY: [22, 0],
      duration: 550,
      delay: anime.stagger(90),
      easing: "easeOutBack",
    });
  }, [showFuture, futureData]);

  if (!showFuture) {
    return (
      <div className="mt-8 text-center" data-testid="av-future-cta">
        <button
          onClick={revealFuture}
          disabled={futureBusy}
          className="inline-flex items-center gap-2 rounded-full px-6 py-3 bg-gradient-to-r from-[#3F2D1B] via-[#7c4f2a] to-[#3F2D1B] text-amber-100 border border-amber-500/40 shadow-lg hover:scale-[1.02] transition-transform"
          data-testid="av-see-future-btn"
        >
          <FastForward size={18} weight="fill" />
          {futureBusy ? "Loading your future arc…" : "See my future journey"}
        </button>
        <div className="mt-2 text-[11px] text-muted">
          Reveal the next 6 mahadashas — same visual language, forward in time.
        </div>
      </div>
    );
  }
  if (!futureData || futureData.length === 0) {
    return <div className="mt-8 text-center text-sm text-muted">No future mahadashas computed.</div>;
  }
  return (
    <div className="mt-8" ref={containerRef} data-testid="av-future-section">
      <h3 className="font-serif-display text-2xl text-[#3F2D1B] text-center">Your future — the road ahead</h3>
      <p className="text-center text-sm text-muted mt-1">
        Next 6 mahadashas, in chronological order. Tap any to peek.
      </p>
      <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {futureData.map((sp, i) => {
          const pal = PALETTES[sp.palette] || PALETTES.jupiter;
          const isActive = selectedFutureIndex === i;
          return (
            <button
              key={i}
              type="button"
              data-future-card
              onClick={() => {
                setSelectedFutureIndex?.(i);
                // Game feedback: unlock sound + achievement toast
                try {
                  playGameSound("unlock");
                  toast.success(`✨ Future Level Unlocked · ${sp.planet}`, { duration: 2200 });
                  // Screen shake on body
                  const body = document.body;
                  body.animate(
                    [{ transform: "translateX(0)" }, { transform: "translateX(-4px)" }, { transform: "translateX(4px)" }, { transform: "translateX(0)" }],
                    { duration: 260, easing: "ease-out" }
                  );
                } catch (_) { /* noop */ }
                // Scroll to the detail card
                setTimeout(() => {
                  document.querySelector('[data-testid="av-detail-card"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
                }, 100);
              }}
              className="rounded-2xl p-4 border shadow text-left w-full hover:scale-[1.02] transition-transform"
              style={{
                background: `linear-gradient(135deg, ${pal.tintFrom} 0%, ${pal.tintTo}80 100%)`,
                borderColor: isActive ? pal.accent : pal.ring + "66",
                borderWidth: isActive ? 2 : 1,
                boxShadow: isActive ? `0 0 22px ${pal.accent}66` : undefined,
                color: pal.ink,
              }}
              data-testid={`av-future-card-${i}`}
            >
              <div className="flex items-baseline justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-widest" style={{ color: pal.accent }}>
                    Age {sp.stage.age_band}
                  </div>
                  <div className="font-serif-display text-xl">{sp.planet}</div>
                </div>
                <div className="text-3xl opacity-80" style={{ color: pal.accent }}>{pal.glyph}</div>
              </div>
              <div className="text-xs opacity-70 mt-1">{sp.from} → {sp.to}</div>
              <div className="text-xs mt-2 italic opacity-80">{sp.tagline}</div>
              <div className="text-[10px] mt-2 opacity-70">{sp.placement}</div>
              <div className="mt-2 border-t border-white/10 pt-2 text-xs">
                <b style={{ color: pal.accent }}>Coming up:</b> {sp.headline}
              </div>
              <div className="mt-2 text-[10px] opacity-70 flex items-center gap-1">
                <Eye size={11} weight="fill" /> Tap to see visual
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Cinematic backdrop — painted SVG (sky + sun + mountains + city + hills)
// Palette-responsive: the sky-glow tint follows the currently selected mahadasha.
// Zero external image cost.
// ----------------------------------------------------------------------------
function CinematicBackdrop({ palette }) {
  return (
    <div className="absolute inset-0 overflow-hidden">
      <svg
        viewBox="0 0 1600 900"
        preserveAspectRatio="xMidYMid slice"
        className="w-full h-full"
      >
        <defs>
          <linearGradient id="sky" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0" stopColor="#0a0a2a" />
            <stop offset="0.35" stopColor={palette.tintFrom} />
            <stop offset="0.65" stopColor={palette.tintTo} />
            <stop offset="1" stopColor="#0a0510" />
          </linearGradient>
          <radialGradient id="sun" cx="0.72" cy="0.55" r="0.16">
            <stop offset="0" stopColor={palette.accent} stopOpacity="1" />
            <stop offset="0.4" stopColor={palette.accent} stopOpacity="0.6" />
            <stop offset="1" stopColor={palette.accent} stopOpacity="0" />
          </radialGradient>
          <linearGradient id="fogFar" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0" stopColor={palette.tintTo} stopOpacity="0.35" />
            <stop offset="1" stopColor="#000" stopOpacity="0.9" />
          </linearGradient>
        </defs>
        {/* Sky */}
        <rect x="0" y="0" width="1600" height="900" fill="url(#sky)" />
        {/* Sun disc + halo */}
        <rect x="0" y="0" width="1600" height="900" fill="url(#sun)" />
        <circle cx="1155" cy="500" r="60" fill={palette.accent} opacity="0.9" />
        <circle cx="1155" cy="500" r="90" fill={palette.accent} opacity="0.2" />

        {/* Distant city skyline silhouette (right side) */}
        <g fill="#000" opacity="0.75">
          <path d="M900 620 L920 620 L920 560 L950 560 L950 540 L960 540 L960 590 L985 590 L985 505 L1005 505 L1005 590 L1025 590 L1025 545 L1050 545 L1050 610 L1080 610 L1080 560 L1100 560 L1100 620 L1120 620 L1120 500 L1145 500 L1145 620 L1170 620 L1170 555 L1190 555 L1190 620 L1215 620 L1215 585 L1245 585 L1245 620 L1275 620 L1275 545 L1300 545 L1300 620 L1330 620 L1330 590 L1355 590 L1355 620 L1385 620 L1385 560 L1410 560 L1410 620 L1440 620 L1440 600 L1470 600 L1470 620 L1500 620 L1500 570 L1525 570 L1525 620 L1600 620 L1600 900 L900 900 Z" />
        </g>
        {/* Faint window lights on the skyline */}
        <g fill={palette.accent} opacity="0.6">
          {Array.from({ length: 45 }).map((_, i) => {
            const seed = (i * 9301 + 49297) % 233280;
            const x = 900 + ((seed >> 4) % 700);
            const y = 520 + ((seed >> 8) % 90);
            const w = 2, h = 3;
            return <rect key={i} x={x} y={y} width={w} height={h} />;
          })}
        </g>

        {/* Far mountain layer */}
        <path
          d="M0 640 L120 560 L260 610 L400 540 L560 610 L720 555 L900 620 L1080 590 L1240 620 L1400 570 L1600 610 L1600 900 L0 900 Z"
          fill="url(#fogFar)"
        />
        {/* Mid mountains */}
        <path
          d="M0 720 L140 640 L280 700 L440 620 L620 700 L800 650 L960 720 L1140 660 L1320 720 L1500 660 L1600 700 L1600 900 L0 900 Z"
          fill="#0a0f1a"
          opacity="0.9"
        />
        {/* Foreground hills (left, casting shadow over kid) */}
        <path
          d="M-40 800 L120 730 L280 780 L420 700 L560 780 L700 720 L720 900 L-40 900 Z"
          fill="#050810"
          opacity="0.95"
        />
        {/* Vegetation dots (bushes) */}
        <g fill="#000" opacity="0.75">
          <ellipse cx="180" cy="790" rx="26" ry="10" />
          <ellipse cx="260" cy="810" rx="34" ry="12" />
          <ellipse cx="360" cy="780" rx="22" ry="9" />
        </g>
      </svg>
    </div>
  );
}


function StarField({ dense = false, palette }) {
  const count = dense ? 60 : 30;
  const stars = Array.from({ length: count });
  const color = palette?.accent || "#ffffff";
  return (
    <div className="absolute inset-0 pointer-events-none" aria-hidden>
      {stars.map((_, i) => {
        const top = Math.random() * 100;
        const left = Math.random() * 100;
        const size = Math.random() * 2 + 1;
        const delay = Math.random() * 4;
        const opacity = 0.3 + Math.random() * 0.6;
        return (
          <div
            key={i}
            style={{
              position: "absolute", top: `${top}%`, left: `${left}%`,
              width: `${size}px`, height: `${size}px`, borderRadius: "50%",
              background: color, opacity,
              animation: `avTwinkle 3s ${delay}s infinite ease-in-out`,
              boxShadow: `0 0 ${size * 2}px ${color}`,
            }}
          />
        );
      })}
      <style>{`
        @keyframes avTwinkle {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50%      { opacity: 1;   transform: scale(1.6); }
        }
      `}</style>
    </div>
  );
}
